import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Layout = ({ children, currentUser }) => {
  const isSimple = currentUser?.isSimple || false;
  const location = useLocation();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fechar menu mobile quando a rota mudar
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Bloquear scroll do body quando menu mobile estiver aberto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = Math.abs(currentScrollY - lastScrollY);
          
          if (currentScrollY < 20) {
            setIsHeaderVisible(true);
            setLastScrollY(currentScrollY);
            ticking = false;
            return;
          }

          if (scrollDifference < 5) {
            ticking = false;
            return;
          }

          if (currentScrollY > lastScrollY && currentScrollY > 80) {
            setIsHeaderVisible(false);
          } else if (currentScrollY < lastScrollY) {
            setIsHeaderVisible(true);
          }

          setLastScrollY(currentScrollY);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Links do menu
  const menuLinks = [
    { to: '/goals', icon: 'fa-list', text: 'Metas', title: 'Metas' },
    { to: '/dashboard', icon: 'fa-chart-line', text: 'Dashboard', title: 'Dashboard' },
    { to: '/goals/create', icon: 'fa-plus', text: 'Nova Meta', title: 'Nova Meta', className: 'btn-new-goal' },
    { to: '/departments', icon: 'fa-building', text: 'Departamentos', title: 'Departamentos' },
    { to: '/matrix', icon: 'fa-table', text: 'Matriz', title: 'Matriz de Metas' },
    ...(!isSimple ? [{ to: '/users', icon: 'fa-users-cog', text: 'Usuários', title: 'Gerenciar Usuários' }] : []),
  ];

  return (
    <div className="goals-app">
      {/* Navigation */}
      <nav className={`goals-nav ${isHeaderVisible ? 'header-visible' : 'header-hidden'}`} role="navigation">
        <div className="goals-nav-container">
          {/* Brand */}
          <div className="goals-nav-brand">
            <Link to="/dashboard" className="goals-nav-link goals-nav-brand-link" title="Início">
              <img src="/logo.png" alt="Logo" className="goals-nav-logo" />
              <span className="goals-nav-title">Sistema de Metas</span>
            </Link>
          </div>

          {/* Tablet Dropdown Menu (768px - 1199px) */}
          <div className="goals-nav-menu-dropdown">
            <div className="dropdown">
              <button 
                className="goals-nav-link dropdown-toggle" 
                type="button" 
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fas fa-bars"></i>
                <span>Menu</span>
              </button>
              <ul className="dropdown-menu">
                {menuLinks.map(link => (
                  <li key={link.to}>
                    <Link 
                      to={link.to} 
                      className={`dropdown-item ${isActive(link.to) ? 'active' : ''}`}
                    >
                      <i className={`fas ${link.icon}`}></i>
                      {link.text}
                    </Link>
                  </li>
                ))}
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button 
                    className="dropdown-item" 
                    onClick={() => {
                      localStorage.removeItem('token');
                      window.location.href = '/';
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Sair
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Desktop Menu (≥1200px) */}
          <div className="goals-nav-menu" id="mobileMenu">
            {menuLinks.map(link => (
              <Link 
                key={link.to}
                to={link.to} 
                className={`goals-nav-link ${link.className || ''} ${isActive(link.to) ? 'active' : ''}`} 
                title={link.title}
              >
                <i className={`fas ${link.icon}`}></i>
                <span className="goals-nav-text">{link.text}</span>
              </Link>
            ))}

            {/* User Menu - Desktop */}
            <div className="goals-nav-user">
              <div className="dropdown">
                <button 
                  className="goals-nav-link dropdown-toggle" 
                  type="button" 
                  id="userMenu" 
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fas fa-user"></i>
                  <span className="d-none d-md-inline">Usuário</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userMenu">
                  <li>
                    <button 
                      className="dropdown-item" 
                      onClick={() => {
                        localStorage.removeItem('token');
                        window.location.href = '/';
                      }}
                    >
                      <i className="fas fa-sign-out-alt"></i> Sair
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Mobile Toggle (≤767px) */}
          <button 
            className="goals-nav-toggle" 
            type="button" 
            onClick={toggleMobileMenu}
            aria-label="Abrir menu"
            aria-expanded={mobileMenuOpen}
          >
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Sheet (≤767px) */}
      <div 
        className={`goals-nav-mobile-sheet ${mobileMenuOpen ? 'show' : ''}`}
        id="mobileMenuSheet"
      >
        {menuLinks.map(link => (
          <Link 
            key={link.to}
            to={link.to} 
            className={`goals-nav-mobile-link ${isActive(link.to) ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <i className={`fas ${link.icon}`}></i>
            <span>{link.text}</span>
          </Link>
        ))}
        
        <div className="goals-nav-mobile-divider"></div>
        
        <button 
          className="goals-nav-mobile-link logout"
          onClick={() => {
            localStorage.removeItem('token');
            window.location.href = '/';
          }}
        >
          <i className="fas fa-sign-out-alt"></i>
          <span>Sair</span>
        </button>
      </div>

      {/* Backdrop do mobile sheet */}
      <div 
        className={`goals-nav-menu-backdrop ${mobileMenuOpen ? 'show' : ''}`} 
        onClick={closeMobileMenu}
      ></div>

      {/* Messages - será usado para exibir mensagens de sucesso/erro */}
      <div id="messages-container"></div>

      {/* Main Content */}
      <main className="goals-main">
        {children}
      </main>

      {/* Footer */}
      <footer className="goals-footer">
        <div className="goals-footer-container">
          <p>&copy; 2025 Metas Crescer</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
